import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import PdMark from './PdMark';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../lib/api';
import { useNotifications } from '../hooks/useNotifications';
import { FeatureIdeaBox } from './FeatureIdeaBox';
import { QuickExitBox } from './QuickExitBox';
import { quickExit, useQuickExit } from '../lib/quickExit';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { LogOut, LayoutGrid, BookOpen, Send, Building2, FileText, Bookmark } from 'lucide-react';

/**
 * The signed-in page frame: a quiet left rail on the ground colour and one
 * white panel for the page. Under 900px the rail becomes a bottom tab bar.
 *
 * Nav is ordered by how often a care leaver needs it. A section only ever
 * draws the eye for something that needs *them*: a breach pill (a hard
 * number, past-deadline) or a small brand dot (a quieter "something
 * changed" — an organisation replied, sent something, or asked for ID; or,
 * on Organisations, something new to look at since one was last opened).
 * There is no separate notification tray; the dashboard's "To do" list
 * and "Recent activity" feed carry the detail.
 */
const NAV = [
  { to: '/dashboard', label: 'Overview', end: true, icon: LayoutGrid },
  { to: '/my-life', label: 'My life', icon: BookOpen },
  { to: '/collections', label: 'Collections', icon: Bookmark },
  { to: '/sar', label: 'Requests', badge: 'needs_you', icon: Send },
  { to: '/organisations', label: 'Organisations', badge: 'news', icon: Building2 },
  { to: '/documents', label: 'Documents', icon: FileText },
];

// One fetch per page load is plenty for a badge; share it across mounts.
let statsCache = { at: 0, value: null };

/** Requests past their deadline, and organisations with something new since last opened. */
const useNavCounts = () => {
  const [counts, setCounts] = useState(statsCache.value);
  useEffect(() => {
    let live = true;
    const fresh = Date.now() - statsCache.at < 30_000;
    if (fresh && statsCache.value !== null) return undefined;
    dashboardApi.getStats().then((res) => {
      const s = res.data || {};
      const value = { overdue: s.overdue || 0, news: s.organisations_with_news || 0 };
      statsCache = { at: Date.now(), value };
      if (live) setCounts(value);
    }).catch(() => { /* badge is best-effort */ });
    return () => { live = false; };
  }, []);
  return counts || { overdue: 0, news: 0 };
};

/** Opening an organisation clears its dot, so the nav's count is re-read next time. */
export const forgetNavCounts = () => { statsCache = { at: 0, value: null }; };

/** Does any request have an update the user hasn't seen yet — a reply, a document, an ID ask? */
const useHasRequestUpdate = () => {
  const { notifications } = useNotifications();
  return notifications.some((n) => !n.read && n.sar_id);
};

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');

const railItem = ({ isActive }) =>
  `flex items-center justify-between rounded-[10px] px-[13px] py-[11px] text-[15px] transition-colors duration-state ${
    isActive ? 'bg-surface font-semibold text-ink' : 'text-ink-55 hover:text-ink hover:bg-surface/60'
  }`;

const tabItem = ({ isActive }) =>
  `relative flex flex-1 flex-col items-center justify-center gap-1 min-h-[48px] px-1 py-1 text-[11px] leading-tight text-center transition-colors duration-state ${
    isActive ? 'font-semibold text-brand' : 'text-ink-55'
  }`;

/**
 * Every signed-in page is one of two widths, always centred in the panel —
 * pages never set their own max-width or margin (see DESIGN.md "Page layout").
 *   wide   — the default: section pages, lists, dashboards, detail pages.
 *   narrow — a single column of form fields or settings (Profile, Suggestions).
 */
const PAGE_WIDTHS = {
  wide: 'max-w-7xl',
  narrow: 'max-w-3xl',
};

export const AppShell = ({ children, width = 'wide' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { overdue: needsYou, news } = useNavCounts();
  const hasUpdate = useHasRequestUpdate();
  useQuickExit();
  // What each section's badge shows: a count past deadline, or a quiet dot.
  const badgeFor = (item) => {
    if (item.badge === 'needs_you') {
      if (needsYou > 0) return { count: needsYou, label: `${needsYou} past deadline` };
      if (hasUpdate) return { dot: true, label: 'An update is waiting for you' };
    }
    if (item.badge === 'news' && news > 0) {
      return { dot: true, label: `Something new to look at from ${news} organisation${news === 1 ? '' : 's'}` };
    }
    return null;
  };
  const items = user?.role === 'admin' ? [...NAV, { to: '/admin', label: 'Admin' }] : NAV;
  const firstName = user?.full_name?.split(' ')[0] || 'You';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const Badge = ({ item }) => {
    const b = badgeFor(item);
    if (!b) return null;
    if (b.count) {
      return (
        <span
          className="ml-2 inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-breach px-1.5 text-[12px] font-semibold text-white"
          aria-label={b.label}
          data-testid="nav-needs-you-count"
        >
          {b.count}
        </span>
      );
    }
    return (
      <span className="ml-2 inline-flex items-center" aria-label={b.label} data-testid={`nav-dot-${item.badge}`}>
        <span aria-hidden="true" className="w-[8px] h-[8px] rounded-full bg-brand" />
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-ground flex flex-col min-[900px]:flex-row">
      {/* Left rail (≥900px) */}
      <aside className="hidden min-[900px]:flex w-[232px] shrink-0 flex-col px-[14px] pt-[22px] pb-[18px] sticky top-0 h-screen" aria-label="Primary">
        <div className="px-[6px] mb-6">
          <Link to="/dashboard" className="flex items-center gap-3 rounded-md" data-testid="header-home-link">
            <PdMark size={28} className="text-brand" />
            <span className="font-heading text-[17px] font-bold tracking-[-0.02em] text-ink whitespace-nowrap">Precious Data</span>
          </Link>
        </div>

        <nav className="flex flex-col gap-0.5" aria-label="Sections">
          {items.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={railItem} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <span>{item.label}</span>
              <Badge item={item} />
            </NavLink>
          ))}
        </nav>

        <QuickExitBox />
        <FeatureIdeaBox />

        <div className="mt-auto pt-6 flex items-center justify-between gap-2">
          <Link to="/profile" className="flex items-center gap-3 rounded-md min-w-0" data-testid="nav-profile">
            <Avatar className="h-9 w-9">
              {user?.avatar_url ? <AvatarImage src={user.avatar_url} alt="" /> : null}
              <AvatarFallback className="bg-avatar text-ink text-sm font-semibold">{initials(user?.full_name) || 'PD'}</AvatarFallback>
            </Avatar>
            <span className="truncate text-[15px] font-medium text-ink">{firstName}</span>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-55 hover:text-ink hover:bg-surface/60 transition-colors duration-state"
            aria-label="Sign out"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </aside>

      {/* Compact top bar (<900px) */}
      <header className="min-[900px]:hidden flex items-center justify-between px-4 py-3">
        <Link to="/dashboard" className="flex items-center gap-2.5" aria-label="Precious Data home">
          <PdMark size={28} className="text-brand" />
          <span className="font-heading text-[17px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
        </Link>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={quickExit}
            className="mr-1 inline-flex min-h-[40px] items-center rounded-md px-2 text-[13px] font-semibold text-brand"
            data-testid="quick-exit-btn-compact"
          >
            Leave quickly
          </button>
          <Link to="/profile" className="rounded-full" aria-label="Your profile">
            <Avatar className="h-9 w-9">
              {user?.avatar_url ? <AvatarImage src={user.avatar_url} alt="" /> : null}
              <AvatarFallback className="bg-avatar text-ink text-sm font-semibold">{initials(user?.full_name) || 'PD'}</AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </header>

      {/* Page panel */}
      <div className="flex-1 min-w-0 min-[900px]:py-[14px] min-[900px]:pr-[14px] pb-[104px] min-[900px]:pb-[14px]">
        <div className="bg-surface min-h-full rounded-none min-[900px]:rounded-xl px-5 py-6 min-[900px]:px-9 min-[900px]:py-[34px]">
          <div className={`mx-auto w-full ${PAGE_WIDTHS[width] || PAGE_WIDTHS.wide}`} data-page-width={width}>
            {children}
          </div>
        </div>
      </div>

      {/* Bottom tabs (<900px) — a floating card, not a flush bar */}
      <div className="min-[900px]:hidden fixed bottom-0 inset-x-0 z-40 px-3 pt-2 pb-[max(10px,env(safe-area-inset-bottom))] pointer-events-none">
        <nav
          className="pointer-events-auto flex items-center gap-1 bg-surface rounded-lg shadow-overlay px-2 py-2"
          aria-label="Sections"
        >
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} className={tabItem}>
                {({ isActive }) => (
                  <>
                    <span className={`flex items-center justify-center w-10 h-6 rounded-full transition-colors duration-state ${isActive ? 'bg-brand-wash' : ''}`}>
                      <Icon className="w-[20px] h-[20px]" strokeWidth={1.75} aria-hidden="true" />
                    </span>
                    <span>{item.label}</span>
                    {(() => {
                      const b = badgeFor(item);
                      if (!b) return null;
                      return b.count ? (
                        <span
                          className="absolute top-0 right-2 min-w-[18px] h-[18px] rounded-full bg-breach px-1 text-[11px] font-semibold text-white flex items-center justify-center"
                          aria-label={b.label}
                        >
                          {b.count}
                        </span>
                      ) : (
                        <span aria-label={b.label} className="absolute top-0.5 right-3 w-[8px] h-[8px] rounded-full bg-brand" />
                      );
                    })()}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default AppShell;
